import * as R from 'ramda';
import FileSaver from 'file-saver';
import FileDownload from 'js-file-download';
import imageCompression from 'browser-image-compression';
// constants
import * as G from '../helpers';
import * as GC from '../constants';
// helpers
import * as H from './helpers';
//////////////////////////////////////////////////

const openFile = (file) => {
  const link = document.createElement('a');

  link.target = '_blank';
  link.href = window.URL.createObjectURL(file);

  link.click();
  link.remove();
};

const openFileByPublicLink = (href) => {
  const link = document.createElement('a');

  link.target = '_blank';
  link.href = href;

  link.click();
  link.remove();
};

const makeDataForDocument = (payload) => {
  const data = new window.FormData();

  data.append('file', payload.file);
  data.append('data', JSON.stringify(payload));

  return data;
};

const makeDataForMultipleDocuments = (payload) => {
  const data = new window.FormData();

  const { files } = payload;

  if (H.isNotNilAndNotEmpty(files)) {
    payload.files.forEach((file) => data.append('files', file));
  }

  data.append('data', JSON.stringify(payload));

  data.append('deleteFile', R.propOr(false, 'deleteFile', payload));

  return data;
};

const makeDataForMultipleDocumentsBatch = (payload) => {
  const data = new window.FormData();

  const { files, documentTypes, primaryObjectGuid } = payload;

  if (H.isNotNilAndNotEmpty(files)) {
    payload.files.forEach((file) => data.append('files', file));
  }

  data.append('documentTypes', G.createStringFromArray(documentTypes, ','));

  data.append('primaryObjectGuid', primaryObjectGuid);

  data.append('deleteFile', R.propOr(false, 'deleteFile', payload));

  return data;
};

const downloadFile = (data, name) => FileDownload(data, name);

const downloadFileFromUrl = (url, fileName) => {
  const tempLink = document.createElement('a');

  tempLink.href = url;
  tempLink.download = fileName;
  tempLink.style.display = 'none';

  // Safari thinks _blank anchor are pop ups. We only want to set _blank
  // target if the browser does not support the HTML5 download attribute.
  // This allows you to download files in desktop safari if pop up blocking
  // is enabled.
  if (typeof tempLink.download === 'undefined') {
    tempLink.setAttribute('target', '_blank');
    tempLink.setAttribute('rel', 'noopener noreferrer');
  }

  document.body.appendChild(tempLink);

  tempLink.click();

  document.body.removeChild(tempLink);
};

const getPrintDocumentOptions = R.compose(
  R.map(({ guid, comments, documentType, documentFilename }) => ({
    [GC.FIELD_VALUE]: guid,
    [GC.FIELD_LABEL]: G.createStringFromArray(
      [G.getDisplayedValueFromObject(documentType), documentFilename, comments],
      ' - ',
    ),
  })),
  R.filter((document) => {
    const fileExtension = R.last(R.split('.', R.pathOr('', [GC.FIELD_DOCUMENT_FILE_NAME], document)));

    return R.includes(fileExtension, GC.DOCUMENTS_EXTENSIONS);
  }),
);

const getFileNameFromResponseDispositionHeader = (request, defaultFileName) => {
  if (H.isNilOrEmpty(request)) return defaultFileName;

  let filename = defaultFileName;

  const disposition = request.getResponseHeader('content-disposition');

  const condition = H.isNotNilAndNotEmpty(disposition) && H.notEquals(R.indexOf('attachment', disposition), -1);

  if (condition) {
    const matches = GC.FILE_NAME_REGEXP.exec(disposition);

    if (H.isNotNil(R.path([1], matches))) {
      filename = R.path([1], matches).replace(/['"]/g, '');
    }
  }

  return filename;
};

const saveFileFromResponse = (response, defaultFileName = null) => {
  const filename = getFileNameFromResponseDispositionHeader(response.request, defaultFileName);

  const type = R.pathOr('application/pdf', ['headers', 'content-type'], response);

  const file = new window.Blob(R.of(Array, response.data), { type });

  FileSaver.saveAs(file, filename);
};

const openFileInWindowFromArrayBufferResponse = (res, options) => {
  const { data, headers, request } = res;

  const defaultFileName = R.path(['defaultFileName'], options);
  const sizes = R.pathOr('width:200,height:200', ['sizes'], options);
  const type = R.pathOr('application/octet-stream', ['content-type'], headers);

  const fileName = getFileNameFromResponseDispositionHeader(request, defaultFileName);

  const blob = new Blob([data], { type }); // eslint-disable-line
  const blobURL = window.URL.createObjectURL(blob);

  window.open(blobURL, R.or(fileName, 'Preview'), sizes);
  window.URL.revokeObjectURL(blobURL);
};

const getImageSrcFromArrayBufferResponse = (res) => {
  const { data, headers } = res;

  const type = R.pathOr('application/octet-stream', ['content-type'], headers);

  const blob = new Blob([data], { type }); // eslint-disable-line

  return window.URL.createObjectURL(blob);
};

const getFileInstanceFromArrayBufferResponse = (response, defaultFileName = null) => {
  const { data, request, headers } = response;

  const fileName = getFileNameFromResponseDispositionHeader(request, defaultFileName);

  const type = R.pathOr('application/octet-stream', ['content-type'], headers);

  const file = new File([data], fileName, { type }); // eslint-disable-line

  return file;
};

// array-buffer
const decodeArrayBuffer = (file) => {
  if (R.not('TextDecoder' in window)) {
    return 'Sorry, this browser does not support TextDecoder...';
  }

  const decoder = new TextDecoder('utf-8'); // eslint-disable-line

  const text = decoder.decode(file);

  return R.map(R.trim, R.split('~', text));
};

const convertArrayBufferFailResponse = (res) => {
  const decodedString = String.fromCharCode.apply(null, new Uint8Array(res.data));

  const obj = JSON.parse(decodedString);

  const { message } = obj;

  return R.assoc('data', { message }, res);
};

const createObjectURL = (file) => URL.createObjectURL(file); // eslint-disable-line

const maxImageSize = 5 * 10e5;

async function compressFileIfImageTooLarge(file) {
  if (R.and(
    file.type.match(/image.*/),
    file.size > maxImageSize,
  )) {
    try {
      const options = {
        maxSizeMB: 5,
      };

      const blob = await imageCompression(file, options);

      return new File([blob], file.name, { type: blob.type });
    } catch (error) {
      console.error('Error compressing the image:', error);
    }
  }

  return file;
}
const getFileMimeType = (filename) => {
  if (!filename || typeof filename !== 'string') return 'unknown';

  const extension = filename.split('.').pop().toLowerCase();

  const mimeTypes = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    bmp: 'image/bmp',
    svg: 'image/svg+xml',
    webp: 'image/webp',
    pdf: 'application/pdf',
    txt: 'text/plain',
    html: 'text/html',
    json: 'application/json',
    js: 'application/javascript',
    css: 'text/css',
  };

  return R.propOr('application/octet-stream', extension, mimeTypes);
};

export {
  openFile,
  downloadFile,
  createObjectURL,
  getFileMimeType,
  decodeArrayBuffer,
  downloadFileFromUrl,
  makeDataForDocument,
  saveFileFromResponse,
  openFileByPublicLink,
  getPrintDocumentOptions,
  compressFileIfImageTooLarge,
  makeDataForMultipleDocuments,
  convertArrayBufferFailResponse,
  makeDataForMultipleDocumentsBatch,
  getImageSrcFromArrayBufferResponse,
  getFileInstanceFromArrayBufferResponse,
  openFileInWindowFromArrayBufferResponse,
  getFileNameFromResponseDispositionHeader,
};
